import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

import {
  fetchRevenueDashboard,
  fetchRevenueDashboardDetails,
  fetchProductComparison,
  fetchPlanDistribution,
  fetchPeriodComparison,
  fetchConversionRate,
  type RevenuePeriod,
  type RevenueDetailCategory,
} from '../services/revenueDashboard.api';
import { Icons } from '@/shared/components/ui/icons';
import { cn } from '@/shared/lib/cn';

const PERIOD_OPTIONS: { value: RevenuePeriod; ar: string; en: string }[] = [
  { value: 'daily', ar: 'يوم', en: 'Day' },
  { value: 'weekly', ar: 'أسبوع', en: 'Week' },
  { value: 'monthly', ar: 'شهري', en: 'Monthly' },
  { value: 'quarterly', ar: 'ربع سنوي', en: 'Quarterly' },
  { value: 'yearly', ar: 'سنوي', en: 'Yearly' },
  { value: 'all', ar: 'الكل', en: 'All time' },
];

const PAGE_SIZE = 25;
const PIE_COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#a855f7', '#ef4444', '#06b6d4'];

function formatMoney(minor: number, isAr: boolean): string {
  return new Intl.NumberFormat(isAr ? 'ar' : 'en', {
    maximumFractionDigits: 2,
  }).format(minor / 100);
}

function formatPct(value: number | null, isAr: boolean): string {
  if (value === null) return isAr ? 'جديد' : 'new';
  const sign = value > 0 ? '+' : '';
  return `${sign}${new Intl.NumberFormat(isAr ? 'ar' : 'en').format(value)}%`;
}

type CardDef = {
  key: RevenueDetailCategory;
  labelAr: string;
  labelEn: string;
  value: number;
  icon: keyof typeof Icons;
  colorClass: string;
};

export function RevenueDashboardPage() {
  const isAr = document.documentElement.lang?.startsWith('ar') ?? true;
  const [period, setPeriod] = useState<RevenuePeriod>('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [expandedCard, setExpandedCard] = useState<RevenueDetailCategory | null>(null);
  const [page, setPage] = useState(1);

  const isCustomRange = period === 'custom';
  const range = isCustomRange
    ? {
        ...(customFrom ? { fromDate: customFrom } : {}),
        ...(customTo ? { toDate: customTo } : {}),
      }
    : undefined;
  const rangeReady = !isCustomRange || Boolean(customFrom && customTo);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'revenue-dashboard', period, customFrom, customTo],
    queryFn: () => fetchRevenueDashboard(period, undefined, range),
    enabled: rangeReady,
  });

  const { data: detailsData, isLoading: detailsLoading } = useQuery({
    queryKey: ['admin', 'revenue-dashboard-details', expandedCard, period, page, customFrom, customTo],
    queryFn: () => fetchRevenueDashboardDetails(expandedCard!, period, page, PAGE_SIZE, range),
    enabled: expandedCard !== null && rangeReady,
  });

  const { data: productComparison } = useQuery({
    queryKey: ['admin', 'revenue-product-comparison', period, customFrom, customTo],
    queryFn: () => fetchProductComparison(period, range),
    enabled: rangeReady,
  });

  const { data: planDistribution } = useQuery({
    queryKey: ['admin', 'revenue-plan-distribution', period, customFrom, customTo],
    queryFn: () => fetchPlanDistribution(period, range),
    enabled: rangeReady,
  });

  const comparablePeriod: RevenuePeriod =
    period === 'all' || period === 'custom' ? 'monthly' : period;
  const { data: periodComparison } = useQuery({
    queryKey: ['admin', 'revenue-period-comparison', comparablePeriod],
    queryFn: () => fetchPeriodComparison(comparablePeriod),
  });

  const { data: conversionRate } = useQuery({
    queryKey: ['admin', 'revenue-conversion-rate', period, customFrom, customTo],
    queryFn: () => fetchConversionRate(period, range),
    enabled: rangeReady,
  });

  const handleSelectCard = (key: RevenueDetailCategory) => {
    setPage(1);
    setExpandedCard(expandedCard === key ? null : key);
  };

  const cards: CardDef[] = data
    ? [
        {
          key: 'free_trial',
          labelAr: 'مشتركين تجربة مجانية',
          labelEn: 'Free Trial Subscribers',
          value: data.summary.free_trial,
          icon: 'Timer',
          colorClass: 'text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400',
        },
        {
          key: 'paid_active',
          labelAr: 'مشتركين دافعين (نشط)',
          labelEn: 'Paid Active Subscribers',
          value: data.summary.paid_active,
          icon: 'CheckCircle',
          colorClass: 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400',
        },
        {
          key: 'pending_payment',
          labelAr: 'بانتظار الدفع',
          labelEn: 'Pending Payment',
          value: data.summary.pending_payment,
          icon: 'DollarSign',
          colorClass: 'text-amber-600 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400',
        },
        {
          key: 'past_due',
          labelAr: 'متأخر السداد',
          labelEn: 'Past Due',
          value: data.summary.past_due,
          icon: 'XCircle',
          colorClass: 'text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400',
        },
        {
          key: 'expiring_in_7_days',
          labelAr: 'ينتهي خلال 7 أيام',
          labelEn: 'Expiring in 7 Days',
          value: data.summary.expiring_in_7_days,
          icon: 'Timer',
          colorClass: 'text-orange-600 bg-orange-100 dark:bg-orange-900/30 dark:text-orange-400',
        },
        {
          key: 'expiring_in_30_days',
          labelAr: 'ينتهي خلال 30 يوم',
          labelEn: 'Expiring in 30 Days',
          value: data.summary.expiring_in_30_days,
          icon: 'Timer',
          colorClass: 'text-purple-600 bg-purple-100 dark:bg-purple-900/30 dark:text-purple-400',
        },
      ]
    : [];

  const activeCardLabel = cards.find((c) => c.key === expandedCard);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-foreground">
          {isAr ? 'لوحة الإيرادات والمشتركين' : 'Revenue & Subscribers Dashboard'}
        </h1>
        <p className="text-sm text-muted-foreground">
          {isAr
            ? 'نظرة شاملة على المشتركين المجانيين والدافعين، وطرق الدفع، والاشتراكات القريبة من الانتهاء'
            : 'Overview of free and paying subscribers, payment methods, and upcoming expirations'}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {PERIOD_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            className={cn(
              'rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors',
              period === opt.value
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border text-muted-foreground hover:bg-muted/60',
            )}
            onClick={() => {
              setPeriod(opt.value);
              setPage(1);
            }}
          >
            {isAr ? opt.ar : opt.en}
          </button>
        ))}
        <button
          type="button"
          className={cn(
            'rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors',
            period === 'custom'
              ? 'border-primary bg-primary/10 text-primary'
              : 'border-border text-muted-foreground hover:bg-muted/60',
          )}
          onClick={() => {
            setPeriod('custom');
            setPage(1);
          }}
        >
          {isAr ? 'مدى مخصص' : 'Custom range'}
        </button>
      </div>

      {isCustomRange ? (
        <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-card p-4">
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            {isAr ? 'من' : 'From'}
            <input
              type="date"
              value={customFrom}
              onChange={(e) => {
                setCustomFrom(e.target.value);
                setPage(1);
              }}
              className="rounded-lg border border-border bg-background px-2 py-1 text-sm text-foreground"
            />
          </label>
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            {isAr ? 'إلى' : 'To'}
            <input
              type="date"
              value={customTo}
              onChange={(e) => {
                setCustomTo(e.target.value);
                setPage(1);
              }}
              className="rounded-lg border border-border bg-background px-2 py-1 text-sm text-foreground"
            />
          </label>
          {!customFrom || !customTo ? (
            <span className="text-xs text-muted-foreground">
              {isAr ? 'اختر التاريخين لعرض النتائج' : 'Pick both dates to see results'}
            </span>
          ) : null}
        </div>
      ) : null}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">{isAr ? 'جارِ التحميل...' : 'Loading...'}</p>
      ) : error ? (
        <p className="text-sm text-destructive">
          {isAr ? 'حدث خطأ أثناء تحميل البيانات' : 'Failed to load data'}
        </p>
      ) : data ? (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {cards.map((card) => {
              const Icon = Icons[card.icon];
              const isActive = expandedCard === card.key;
              return (
                <button
                  key={card.key}
                  type="button"
                  className={cn(
                    'rounded-2xl border bg-card p-4 text-start transition-shadow hover:shadow-md',
                    isActive ? 'border-primary ring-1 ring-primary' : 'border-border',
                  )}
                  onClick={() => handleSelectCard(card.key)}
                >
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm text-muted-foreground">
                      {isAr ? card.labelAr : card.labelEn}
                    </p>
                    <span
                      className={cn(
                        'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl',
                        card.colorClass,
                      )}
                    >
                      <Icon className="h-4 w-4" aria-hidden />
                    </span>
                  </div>
                  <p className="mt-2 text-3xl font-bold text-foreground">{card.value}</p>
                </button>
              );
            })}
          </div>

          {expandedCard ? (
            <div className="rounded-2xl border border-primary/40 bg-card p-4">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-foreground">
                  {isAr ? activeCardLabel?.labelAr : activeCardLabel?.labelEn} —{' '}
                  {detailsData?.total ?? 0} {isAr ? 'مشترك' : 'subscribers'}
                </h2>
                <button
                  type="button"
                  className="text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => setExpandedCard(null)}
                >
                  {isAr ? 'إغلاق' : 'Close'}
                </button>
              </div>

              {detailsLoading ? (
                <p className="text-sm text-muted-foreground">
                  {isAr ? 'جارِ التحميل...' : 'Loading...'}
                </p>
              ) : !detailsData?.items.length ? (
                <p className="text-sm text-muted-foreground">
                  {isAr ? 'لا يوجد مشتركين في هذه الفئة' : 'No subscribers in this category'}
                </p>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-start text-sm">
                      <thead>
                        <tr className="border-b border-border text-xs text-muted-foreground">
                          <th className="py-2 pe-4 font-medium">
                            {isAr ? 'الشركة / المستأجر' : 'Tenant'}
                          </th>
                          <th className="py-2 pe-4 font-medium">{isAr ? 'المنتج' : 'Product'}</th>
                          <th className="py-2 pe-4 font-medium">{isAr ? 'الخطة' : 'Plan'}</th>
                          <th className="py-2 pe-4 font-medium">{isAr ? 'الحالة' : 'Status'}</th>
                          <th className="py-2 pe-4 font-medium">{isAr ? 'الإيميل' : 'Email'}</th>
                          <th className="py-2 font-medium">{isAr ? 'رقم التليفون' : 'Phone'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailsData.items.map((item) => (
                          <tr key={item.id} className="border-b border-border/60">
                            <td className="py-2 pe-4 text-foreground">{item.tenant_name}</td>
                            <td className="py-2 pe-4 text-muted-foreground">
                              {item.product_name}
                            </td>
                            <td className="py-2 pe-4 text-muted-foreground">{item.plan_name}</td>
                            <td className="py-2 pe-4 text-muted-foreground">{item.status}</td>
                            <td className="py-2 pe-4 text-muted-foreground" dir="ltr">
                              {item.email ?? '—'}
                            </td>
                            <td className="py-2 text-muted-foreground" dir="ltr">
                              {item.phone ?? '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {detailsData.total_pages > 1 ? (
                    <div className="mt-4 flex items-center justify-between">
                      <p className="text-xs text-muted-foreground">
                        {isAr
                          ? `صفحة ${detailsData.page} من ${detailsData.total_pages}`
                          : `Page ${detailsData.page} of ${detailsData.total_pages}`}
                      </p>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted/60 disabled:opacity-40"
                          disabled={page <= 1}
                          onClick={() => setPage((p) => Math.max(p - 1, 1))}
                        >
                          {isAr ? 'السابق' : 'Previous'}
                        </button>
                        <button
                          type="button"
                          className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted/60 disabled:opacity-40"
                          disabled={page >= detailsData.total_pages}
                          onClick={() => setPage((p) => Math.min(p + 1, detailsData.total_pages))}
                        >
                          {isAr ? 'التالي' : 'Next'}
                        </button>
                      </div>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          ) : null}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-border bg-card p-4">
              <p className="text-sm font-semibold text-foreground">
                {isAr ? 'مدفوعات InstaPay الناجحة' : 'Successful InstaPay Payments'}
              </p>
              <p className="mt-2 text-2xl font-bold text-foreground">
                {data.payments.instapay.count}
                <span className="ms-2 text-sm font-normal text-muted-foreground">
                  ({formatMoney(data.payments.instapay.total_minor, isAr)})
                </span>
              </p>
            </div>
            <div className="rounded-2xl border border-border bg-card p-4">
              <p className="text-sm font-semibold text-foreground">
                {isAr ? 'مدفوعات MyFatoorah الناجحة' : 'Successful MyFatoorah Payments'}
              </p>
              <p className="mt-2 text-2xl font-bold text-foreground">
                {data.payments.myfatoorah.count}
                <span className="ms-2 text-sm font-normal text-muted-foreground">
                  ({formatMoney(data.payments.myfatoorah.total_minor, isAr)})
                </span>
              </p>
            </div>
          </div>

          {/* ── Period-over-period comparison ── */}
          {periodComparison ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="rounded-2xl border border-border bg-card p-4">
                <p className="text-sm font-semibold text-foreground">
                  {isAr ? 'اشتراكات جديدة مقابل الفترة السابقة' : 'New Subscriptions vs. Previous Period'}
                </p>
                <div className="mt-2 flex items-baseline gap-3">
                  <p className="text-2xl font-bold text-foreground">
                    {periodComparison.current.new_subscriptions}
                  </p>
                  <span
                    className={cn(
                      'text-sm font-medium',
                      (periodComparison.change_pct.new_subscriptions ?? 0) >= 0
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400',
                    )}
                  >
                    {formatPct(periodComparison.change_pct.new_subscriptions, isAr)}
                  </span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {isAr ? 'الفترة السابقة:' : 'Previous period:'}{' '}
                  {periodComparison.previous.new_subscriptions}
                </p>
              </div>
              <div className="rounded-2xl border border-border bg-card p-4">
                <p className="text-sm font-semibold text-foreground">
                  {isAr ? 'الإيرادات مقابل الفترة السابقة' : 'Revenue vs. Previous Period'}
                </p>
                <div className="mt-2 flex items-baseline gap-3">
                  <p className="text-2xl font-bold text-foreground">
                    {formatMoney(periodComparison.current.revenue_minor, isAr)}
                  </p>
                  <span
                    className={cn(
                      'text-sm font-medium',
                      (periodComparison.change_pct.revenue ?? 0) >= 0
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400',
                    )}
                  >
                    {formatPct(periodComparison.change_pct.revenue, isAr)}
                  </span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {isAr ? 'الفترة السابقة:' : 'Previous period:'}{' '}
                  {formatMoney(periodComparison.previous.revenue_minor, isAr)}
                </p>
              </div>
            </div>
          ) : null}

          {/* ── Conversion rate ── */}
          {conversionRate ? (
            <div className="rounded-2xl border border-border bg-card p-4">
              <p className="text-sm font-semibold text-foreground">
                {isAr ? 'معدل التحويل من تجربة إلى دفع فعلي' : 'Trial-to-Paid Conversion Rate'}
              </p>
              <div className="mt-2 flex items-baseline gap-3">
                <p className="text-3xl font-bold text-foreground">
                  {conversionRate.conversion_rate_pct}%
                </p>
                <p className="text-xs text-muted-foreground">
                  {conversionRate.converted_to_paid} / {conversionRate.total_trialed}{' '}
                  {isAr ? 'تحوّلوا لدفع فعلي' : 'converted to paid'}
                </p>
              </div>
            </div>
          ) : null}

          {/* ── Product comparison ── */}
          {productComparison?.products.length ? (
            <div className="rounded-2xl border border-border bg-card p-4">
              <p className="mb-4 text-sm font-semibold text-foreground">
                {isAr ? 'مقارنة المشتركين حسب المنتج' : 'Subscribers by Product'}
              </p>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={productComparison.products}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis
                    dataKey={isAr ? 'product_name_ar' : 'product_name'}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar
                    dataKey="subscriber_count"
                    name={isAr ? 'إجمالي المشتركين' : 'Total Subscribers'}
                    fill="hsl(var(--primary))"
                    radius={[6, 6, 0, 0]}
                  />
                  <Bar
                    dataKey="active_count"
                    name={isAr ? 'المشتركين الدافعين' : 'Active Paid'}
                    fill="#22c55e"
                    radius={[6, 6, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : null}

          {/* ── Plan tier distribution ── */}
          {planDistribution?.tiers.length ? (
            <div className="rounded-2xl border border-border bg-card p-4">
              <p className="mb-4 text-sm font-semibold text-foreground">
                {isAr ? 'توزيع المشتركين حسب الباقة' : 'Subscribers by Plan Tier'}
              </p>
              <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-center sm:justify-center">
                <ResponsiveContainer width="100%" height={220} className="sm:max-w-xs">
                  <PieChart>
                    <Pie
                      data={planDistribution.tiers}
                      dataKey="count"
                      nameKey={isAr ? 'tier_name_ar' : 'tier_name'}
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                    >
                      {planDistribution.tiers.map((_, idx) => (
                        <Cell
                          key={idx}
                          fill={PIE_COLORS[idx % PIE_COLORS.length] ?? '#94a3b8'}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>

                <div className="flex w-full flex-col gap-2 sm:w-auto">
                  {(() => {
                    const total = planDistribution.tiers.reduce((sum, t) => sum + t.count, 0) || 1;
                    return planDistribution.tiers.map((tier, idx) => {
                      const pct = Math.round((tier.count / total) * 100);
                      const color = PIE_COLORS[idx % PIE_COLORS.length] ?? '#94a3b8';
                      return (
                        <div
                          key={tier.tier_slug}
                          className="flex items-center justify-between gap-4 rounded-lg border border-border/60 px-3 py-2"
                        >
                          <div className="flex items-center gap-2">
                            <span
                              className="h-3 w-3 shrink-0 rounded-full"
                              style={{ backgroundColor: color }}
                            />
                            <span className="text-sm text-foreground">
                              {isAr ? tier.tier_name_ar : tier.tier_name}
                            </span>
                          </div>
                          <span className="text-sm font-semibold text-foreground">
                            {tier.count}{' '}
                            <span className="font-normal text-muted-foreground">({pct}%)</span>
                          </span>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            </div>
          ) : null}

          <div className="rounded-2xl border border-border bg-card p-4">
            <p className="mb-4 text-sm font-semibold text-foreground">
              {isAr ? 'اشتراكات جديدة خلال آخر 12 شهر' : 'New Subscriptions Over the Last 12 Months'}
            </p>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={data.monthly_trend}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="hsl(var(--primary))" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      ) : null}
    </div>
  );
}
